/**
 * ONE derivation of "may an operator reassign this order to another bakery"
 * (PRD OPS-002, ALC-010 — finding F-34).
 *
 * The Reassign control used to be gated twice, by hand, and wrongly both
 * times: the Orders row menu disabled it for a settled order or one with no
 * bakery — a condition unrelated to the state machine's legal sources, whose
 * no-bakery half disabled the UNALLOCATED case the control exists for — while
 * the order detail panel never disabled it at all. The control was enabled
 * almost exactly where the server's 409 lived. OPS-002 says a row carries "an
 * overflow menu of the actions that apply to it": enabled must EQUAL legal,
 * and that is only possible when both screens and the server answer the same
 * table.
 *
 * This table restates what `BAKERY_ALLOCATED` is legal from — the reducer's own
 * source list plus the `mayOffer` gate it asks (`packages/domain/src/
 * order-machine.ts`, `order-gates.ts`). Contracts cannot import domain — this
 * package is what the frontends share — so the two are pinned against each
 * other by `reallocation.test.ts` in `packages/services`, which imports both:
 * per status over the two serial regions, and over every REACHABLE state for
 * the money. Per status alone is what let the payment region go unasked here
 * for four days (issue #959, finding 4).
 */

import type { DeliveryPhaseKind, OrderPhase, PaymentPhase, StorefrontMode } from '../enums';
import { deliveryUnbooked } from './delivery-phase';
import { orderPreparationStarted, orderStopped, orderUnderOffer } from './order-phase';
import { paymentHolding } from './payment-phase';
import { boundToOneShop, guestChoosesShop } from './storefront-modes';

/**
 * The statuses `REALLOCATION_STARTED` is legal from. Two families: the
 * recovery states where the automatic paths strand an order, and the live
 * pre-preparation states the operator path opened (PRD 4.2 v8.5). The line is
 * PREPARING: once food is being made or a courier is engaged, reassignment is
 * over — the delivery controls and support cancellation own those.
 */
export function reassignLegal(order: {
  readonly payment: PaymentPhase;
  readonly order: OrderPhase;
  readonly delivery: DeliveryPhaseKind;
}): boolean {
  /*
   * PAY-002, and the region this table forgot (issue #959, finding 4).
   *
   * A reassignment IS an offer: the server ends at `BAKERY_ALLOCATED`, and the
   * machine asks `mayOffer` there, which refuses any order whose money the
   * platform is not holding. This side asked the other two regions and not this
   * one, so the control was enabled on sixteen reachable states the server
   * answers 409 for - every one of them an order the cascade had run out on
   * and that was never paid for.
   * Enabled has to equal legal over STATES, not per status: the cross-product
   * in `reallocation.test.ts` proved two regions agreed and could not see the
   * third, so the walk beside it pins this against the machine directly.
   */
  if (!paymentHolding(order.payment)) return false;
  // An operator may move an order to another counter while nothing has been
  // made for it and no courier is engaged. Past either point the move is not a
  // reassignment, it is a cancellation and a new order.
  if (orderStopped(order.order)) return false;
  if (orderPreparationStarted(order.order)) return false;
  /*
   * "No courier is engaged" - the plan says it as "refused once the DELIVERY is
   * past a price" (1.2), because a booked courier has to be stood down first
   * (SUP-003). Pricing is not engaging: the estimate is taken at allocation, so
   * comparing against `NOT_BOOKED` alone would have disabled Reassign on every
   * order on the board - including the stranded ones the control exists for.
   */
  if (!deliveryUnbooked(order.delivery) && order.delivery !== 'NO_COURIER') return false;
  /*
   * An order nothing has been done with yet has nothing to reassign FROM: the
   * allocation has not run, so there is no counter to move it off. That was
   * `assignment !== 'UNASSIGNED'` and it is the first phase of the workflow.
   *
   * What is left is the plan's list (1.2), restated over the region BAK-044
   * left behind: the three offer rungs (SCHEDULED, ACCEPTANCE_OPEN,
   * ACCEPTANCE_CALLING). The three recovery states that stood beside them -
   * REFUSED, NO_ANSWER, EXHAUSTED - are gone with decisions 8 and 13, and a
   * missed answer re-offers or cancels in the same write, so an operator
   * reassigns an order that is still ON a rung. `PREPARING` is excluded above
   * by `orderPreparationStarted`, which is where a counter's yes lands.
   *
   * A REFUSAL WAITING FOR THE ENGINE IS NOT ONE (#2155). The machine re-offers
   * it, and refuses a reassignment from it: nobody holds it, so there is no
   * rung for the move to keep, and the every-minute engine offers it to the
   * next house within a pass. Asking the rungs by name says that, where
   * `!== 'CREATED'` would have quietly enabled a control the server refuses.
   */
  return orderUnderOffer(order.order);
}

/** ALC-010's fact: the guest picked this house off the mode B vitrine. */
export interface GuestChoiceFacts {
  readonly storefrontMode: StorefrontMode;
  readonly guestSelectedBakery: boolean;
}

/** The wire facts the eligibility question is asked over. */
export interface ReassignFacts extends GuestChoiceFacts {
  readonly payment: PaymentPhase;
  readonly order: OrderPhase;
  readonly delivery: DeliveryPhaseKind;
}

/**
 * True when this order may only move WITHIN the house the guest chose
 * (ALC-010, founder decision 2026-08-24).
 *
 * What the guest picked off a mode B vitrine is a HOUSE — a brand, its
 * catalogue and its prices — and another counter of that same house is still
 * that brand's bread. Moving the order to a different house is not a
 * reassignment of the order, it is a substitution of the thing bought, and it
 * needs the guest's own approval; moving it to a sibling shop needs nobody's.
 *
 * This is the rule the automatic mode B cascade has always followed
 * (`reallocateAfterRejection`, packages/services — `guestApproved` is set for a
 * same-house pick and for nothing else). What was missing was the operator's
 * hand: the manual control refused a guest-chosen order outright, so a shop
 * that telephones in a dead oven could not have its orders moved next door to
 * its own other branch, which is the one move nobody would have to be asked
 * about. Both paths ask this question now.
 *
 * It is a question about WHERE the order may go, not whether it may move at
 * all — which is why `reassignEligible` no longer asks it. A house with one
 * shop is therefore an order with no destination rather than an ineligible
 * one, and the screen says exactly that.
 */
export function reassignConfinedToHouse(facts: GuestChoiceFacts): boolean {
  /*
   * B2 is confined for a different reason and just as hard (founder,
   * 2026-09-20): the platform picked the one shop, the guest was never shown a
   * house, and moving the order to a different brand substitutes the thing
   * bought exactly as it would in mode B. `boundToOneShop` says so by mode
   * rather than by a second `=== 'B2'` here.
   */
  if (boundToOneShop(facts.storefrontMode)) return true;
  return guestChoosesShop(facts.storefrontMode) && facts.guestSelectedBakery;
}

/**
 * True exactly when `POST /internal/orders/:orderId/reassign` can act on this
 * order at all: the status is a legal `REALLOCATION_STARTED` source.
 *
 * ALC-010 is deliberately NOT asked here. It does not decide whether an order
 * can be moved, it decides where to (`reassignConfinedToHouse`) — and while
 * this function answered both, a guest-chosen mode B order got a dead control
 * in every state, including the ones a move within its own house is legal
 * from. The panel that renders it says which of the two stopped it.
 */
export function reassignEligible(facts: ReassignFacts): boolean {
  return reassignLegal(facts);
}
